import { DestroyRef, Directive, ElementRef, Renderer2, inject, AfterViewInit, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl, ValidationErrors } from '@angular/forms';

import { findKey, isNil, isNull, isString, isUndefined } from 'lodash-es';
import { auditTime } from 'rxjs';

import { SearchableSelectComponent } from '../components/searchable-select/searchable-select.component';

@Directive({
  selector:
    'ion-input[redErrorMessage], ion-select[redErrorMessage], ion-textarea[redErrorMessage], red-searchable-select[redErrorMessage]',
  standalone: true
})
export class ErrorMessageDirective implements AfterViewInit {
  readonly redErrorMessage = input<ValidationErrors | null | ''>();

  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly ngControl = inject(NgControl, { self: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchableSelect = inject(SearchableSelectComponent, { optional: true, self: true });

  private readonly errorMessages: Record<string, string> = {
    email: 'Please enter a valid email.',
    emailMatch: 'Please make sure the emails match.',
    invalid: 'The value is invalid.',
    max: 'The value is too big.',
    maxlength: 'The length is too long.',
    min: 'The value is too small.',
    minlength: 'The length is too short.',
    passwordMatch: 'Please make sure the passwords match.',
    pattern: 'The value is invalid.',
    required: 'This field is required.'
  };

  ngAfterViewInit(): void {
    const control = this.ngControl.control;
    if (!isNull(control)) {
      control.valueChanges
        .pipe(auditTime(0), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.updateErrorMessage());

      control.statusChanges
        .pipe(auditTime(0), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.updateErrorMessage());

      this.updateErrorMessage();
    }
  }

  private updateErrorMessage(): void {
    const control = this.ngControl.control;

    if (!isNull(control)) {
      let errorMessage: string | undefined;
      if (control.invalid && control.touched) {
        errorMessage = this.getErrorMessage(control.errors);
      }

      if (this.searchableSelect) {
        this.searchableSelect.errorText = errorMessage;
      } else {
        this.renderer.setProperty(this.elementRef.nativeElement, 'errorText', errorMessage);
      }
    }
  }

  private getErrorMessage(errors: Record<string, unknown> | null) {
    let result: string | undefined;
    if (!isNull(errors)) {
      const errorKey = findKey(errors, (_, key) => key in this.errorMessages);
      if (!isUndefined(errorKey)) {
        result = this.errorMessages[errorKey];
        if (errorKey === 'invalid') {
          const validationErrors = this.redErrorMessage();
          if (!isNil(validationErrors) && !isString(validationErrors) && !isNil(validationErrors['invalid'])) {
            result = validationErrors['invalid'];
          }
        }
      }
    }
    return result;
  }
}
